// src/app/upgrade-subscription.jsx
import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  ActivityIndicator,
} from 'react-native';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import axios from 'axios';
import API_BASE_URL, { STRIPE_PUBLISHABLE_KEY } from '../apiConfig.js';

const stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY);

export default function UpgradeSubscription() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);

  // Check if we're returning from a successful Stripe checkout
useEffect(() => {
  const checkSubscription = async () => {
    const success = searchParams.get('success');
    const canceled = searchParams.get('canceled');

    if (success === 'true') {
      try {
        // Update localStorage permissions to full access (owner level)
        const permissionsStr = localStorage.getItem('userPermissions');
        if (permissionsStr) {
          const permissions = JSON.parse(permissionsStr);
          const updatedPermissions = {
            ...permissions,
            job_perm: 'write',
            invoice_perm: 'write',
            quote_perm: 'write',
            customer_perm: 'write',
            price_perm: 'visible',
            view_all_jobs: 1,
            edit_all_jobs: 1,
            delete_all_jobs: 1,
          };
          localStorage.setItem('userPermissions', JSON.stringify(updatedPermissions));
          console.log('✅ Updated local permissions to full access after subscription upgrade');
        }

        // Fetch updated subscription status from the backend
        const userToken = localStorage.getItem('userToken');
        if (!userToken) {
          console.error('User token is missing. Cannot fetch subscription status.');
          return;
        }

        const response = await axios.get(`${API_BASE_URL}/api/subscription/status`, {
          headers: { Authorization: `Bearer ${userToken}` },
        });

        if (response.data) {
          // Store the updated subscription data
          localStorage.setItem('subscription', response.data.subscription);
          localStorage.setItem('subscriptionLastChecked', new Date().toISOString());
          localStorage.setItem('subscriptionJustUpdated', 'true');
          console.log('✅ Updated subscription and subscriptionLastChecked in localStorage');
        }

        // Clear cached data so it gets refreshed with new subscription status
        const keysToClear = ['activeJobs', 'activeInvoices', 'activeCustomers'];
        keysToClear.forEach((key) => localStorage.removeItem(key));
        console.log('✅ Cleared cached data - will fetch fresh data with new subscription');

        setShowSuccess(true);

        // Redirect to home after 3 seconds
        setTimeout(() => {
          navigate('/');
        }, 3000);
      } catch (error) {
        console.error('Error during subscription upgrade handling:', error);
        alert('An error occurred while updating your subscription. Please try again.');
      }
    } else if (canceled === 'true') {
      alert('Payment was canceled. You can try again anytime.');
    }

    // If user just navigated here without completing checkout, show message
    if (!success && !canceled && searchParams.has('session_id')) {
      alert('Payment was not completed. Please try again or contact support if you need help.');
    }
  };

  checkSubscription();
}, [searchParams, navigate]);

  // If showing success message, display that instead
  if (showSuccess) {
    return (
      <View style={styles.successContainer}>
        <Text style={styles.successTitle}>🎉 Payment Successful!</Text>
        <Text style={styles.successMessage}>
          Your subscription has been activated. Welcome to TradeBook!
        </Text>
        <Text style={styles.successSubtext}>
          Redirecting you to the home page...
        </Text>
        <ActivityIndicator size="large" color="#34C759" style={{ marginTop: 20 }} />
      </View>
    );
  }

  const handleSubscribe = async (planType) => {
    setLoading(true);
    setSelectedPlan(planType);

    try {
      const userToken = localStorage.getItem('userToken');
      
      // Create Checkout Session on your backend
      const response = await axios.post(
        `${API_BASE_URL}/api/create-checkout-session`,
        { planType },
        { headers: { Authorization: `Bearer ${userToken}` } }
      );

      if (!response.data || !response.data.url) {
        alert('Failed to create checkout session. Please try again.');
        return;
      }

      // Redirect directly to the Stripe Checkout URL
      window.location.href = response.data.url;
      
    } catch (error) {
      console.error('Error creating checkout session:', error);
      alert('Failed to start checkout. Please try again.');
      setLoading(false);
      setSelectedPlan(null);
    }
  };

  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.contentContainer}>
      <Text style={styles.title}>Choose Your Plan</Text>
      <Text style={styles.subtitle}>
        Select the plan that best fits your business needs
      </Text>

      {/* Lite Plan */}
      <View style={styles.planCard}>
        <View style={styles.planHeader}>
          <Text style={styles.planName}>TradeBook Lite</Text>
          <Text style={styles.planPrice}>$12.99/month</Text>
        </View>

        <View style={styles.planFeatures}>
          <Text style={styles.featuresTitle}>Perfect for:</Text>
          <Text style={styles.featureText}>• Single-person local service businesses</Text>
          <Text style={styles.featureText}>
            • Solo operators with an administrative assistant
          </Text>
          <Text style={styles.featureText}>• Shared login access (1 user max)</Text>

          <Text style={[styles.featuresTitle, styles.marginTop]}>Features:</Text>
          <Text style={styles.featureText}>✓ Unlimited customers</Text>
          <Text style={styles.featureText}>✓ Unlimited jobs & invoices</Text>
          <Text style={styles.featureText}>✓ Quote management</Text>
          <Text style={styles.featureText}>✓ Schedule & calendar</Text>
          <Text style={styles.featureText}>✓ Mobile & web access</Text>
        </View>

        <TouchableOpacity
          style={[styles.selectButton, loading && selectedPlan === 'lite' && styles.selectButtonDisabled]}
          onPress={() => handleSubscribe('lite')}
          disabled={loading}
        >
          {loading && selectedPlan === 'lite' ? (
            <ActivityIndicator color="#fff" />
          ) : (
            <Text style={styles.selectButtonText}>Select Lite Plan</Text>
          )}
        </TouchableOpacity>
      </View>

      {/* Pro Plan */}
      <View style={[styles.planCard, styles.proPlanCard]}>
        <View style={styles.popularBadge}>
          <Text style={styles.popularBadgeText}>MOST POPULAR</Text>
        </View>

        <View style={styles.planHeader}>
          <Text style={styles.planName}>TradeBook Pro</Text>
          <Text style={styles.planPrice}>$34.99/month</Text>
        </View>

        <View style={styles.planFeatures}>
          <Text style={styles.featuresTitle}>Perfect for:</Text>
          <Text style={styles.featureText}>• Multi-person businesses</Text>
          <Text style={styles.featureText}>• Multiple technicians with separate schedules</Text>
          <Text style={styles.featureText}>• Office personnel & field staff</Text>
          <Text style={styles.featureText}>• Teams requiring job assignments</Text>

          <Text style={[styles.featuresTitle, styles.marginTop]}>All Lite features, plus:</Text>
          <Text style={styles.featureText}>✓ Up to 20 users per account</Text>
          <Text style={styles.featureText}>✓ Individual user permissions</Text>
          <Text style={styles.featureText}>✓ User-specific schedules</Text>
          <Text style={styles.featureText}>✓ Job assignment management</Text>
          <Text style={styles.featureText}>✓ Advanced team collaboration</Text>
          <Text style={styles.featureText}>✓ Priority support</Text>
        </View>

        <TouchableOpacity
          style={[
            styles.selectButton,
            styles.proSelectButton,
            loading && selectedPlan === 'pro' && styles.selectButtonDisabled,
          ]}
          onPress={() => handleSubscribe('pro')}
          disabled={loading}
        >
          {loading && selectedPlan === 'pro' ? (
            <ActivityIndicator color="#fff" />
          ) : (
            <Text style={styles.selectButtonText}>Select Pro Plan</Text>
          )}
        </TouchableOpacity>
      </View>

      <TouchableOpacity 
        style={styles.backButton} 
        onPress={() => navigate(-1)}
        disabled={loading}
      >
        <Text style={styles.backButtonText}>Maybe Later</Text>
      </TouchableOpacity>

      <Text style={styles.disclaimer}>
        • Subscriptions auto-renew monthly unless cancelled{'\n'}
        • Cancel anytime through your account settings{'\n'}
        • Read-only access continues after cancellation{'\n'}
        • Secure payment processing by Stripe
      </Text>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f5f5f5',
  },
  contentContainer: {
    padding: 20,
    paddingBottom: 40,
    maxWidth: 1000,
    marginHorizontal: 'auto',
    width: '100%',
  },
  title: {
    fontSize: 32,
    fontWeight: 'bold',
    textAlign: 'center',
    marginBottom: 10,
    color: '#333',
  },
  subtitle: {
    fontSize: 18,
    textAlign: 'center',
    marginBottom: 30,
    color: '#666',
  },
  planCard: {
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 30,
    marginBottom: 20,
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    position: 'relative',
  },
  proPlanCard: {
    borderWidth: 2,
    borderColor: '#007AFF',
    borderStyle: 'solid',
  },
  popularBadge: {
    position: 'absolute',
    top: -10,
    right: 20,
    backgroundColor: '#FF6B35',
    paddingHorizontal: 12,
    paddingVertical: 4,
    borderRadius: 12,
  },
  popularBadgeText: {
    color: '#fff',
    fontSize: 12,
    fontWeight: 'bold',
  },
  planHeader: {
    marginBottom: 25,
    alignItems: 'center',
  },
  planName: {
    fontSize: 28,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 8,
  },
  planPrice: {
    fontSize: 36,
    fontWeight: 'bold',
    color: '#007AFF',
  },
  planFeatures: {
    marginBottom: 25,
  },
  featuresTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 10,
    marginTop: 5,
  },
  marginTop: {
    marginTop: 20,
  },
  featureText: {
    fontSize: 16,
    color: '#555',
    marginBottom: 8,
    lineHeight: 24,
  },
  selectButton: {
    backgroundColor: '#007AFF',
    paddingVertical: 15,
    paddingHorizontal: 30,
    borderRadius: 8,
    alignItems: 'center',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
  },
  selectButtonDisabled: {
    opacity: 0.6,
    cursor: 'not-allowed',
  },
  proSelectButton: {
    backgroundColor: '#34C759',
  },
  selectButtonText: {
    color: '#fff',
    fontSize: 18,
    fontWeight: 'bold',
  },
  backButton: {
    marginTop: 20,
    paddingVertical: 12,
    alignItems: 'center',
  },
  backButtonText: {
    color: '#007AFF',
    fontSize: 16,
    cursor: 'pointer',
  },
  disclaimer: {
    marginTop: 30,
    fontSize: 13,
    color: '#999',
    textAlign: 'center',
    lineHeight: 20,
  },
  successContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 40,
    backgroundColor: '#f5f5f5',
  },
  successTitle: {
    fontSize: 32,
    fontWeight: 'bold',
    color: '#34C759',
    marginBottom: 20,
    textAlign: 'center',
  },
  successMessage: {
    fontSize: 20,
    color: '#333',
    textAlign: 'center',
    marginBottom: 10,
  },
  successSubtext: {
    fontSize: 16,
    color: '#666',
    textAlign: 'center',
  },
});